import { flexRender } from '@tanstack/react-table';
import cx from 'classnames';
import React, { Fragment } from 'react';
import Pagination from '../../Pagination';
import { InContentAd } from '../../ads/inContentAd';
import { IconButton } from '../../presentational/buttons/IconButton';
import { SpinnerOverlay } from '../../presentational/spinner';
import { ComplexTooltip } from '../../presentational/tooltip';
import { Stack } from '../../stack/Stack';
import { useTableState } from '../lib';
import { DefaultDataType, IDataTableProps, TColumn } from '../types';
import { isInteractiveElement } from '../utils';
import _ from 'lodash';

export function DataTable<T extends DefaultDataType = DefaultDataType>(props: IDataTableProps<T>) {
  const {
    expandableConfig,
    pagination,
    isPaginationVisible,
    tableInstance,
    isEmpty,
    columnsLength,
    expandable,
    getHeaderSorting,
    getRowKey,
    getExpandableRow,
    paginationConfig
  } = useTableState(props);

  const {
    hideHeader,
    className,
    containerClassName,
    isLoading,
    emptyContent,
    getRowProps,
    muteValues,
    wrapperProps,
    data,
    columns,
    footer,
    pagination: propPagination,
    renderToolbar,
    getRowKey: _domUnsafeGetRowKey,
    getCellProps,
    ...restProps
  } = props;
  void _domUnsafeGetRowKey;

  const { showPaginationTop = true, showPaginationBottom = true } = paginationConfig;
  const toolbar = renderToolbar?.({
    tableInstance,
    rows: tableInstance.getPrePaginationRowModel().rows,
    isLoading,
    isEmpty
  });
  const showTopPagination = isPaginationVisible && showPaginationTop;

  return (
    <div {...wrapperProps}>
      <Pagination
        removeVerticalMargins
        top={showTopPagination ? true : toolbar ? <></> : false}
        bottom={isPaginationVisible && showPaginationBottom}
        navButtonsProps={pagination}
        upperChildren={toolbar ? <div className="slds-m-bottom--small">{toolbar}</div> : undefined}
      >
        <div className={containerClassName}>
          <table {...restProps} className={cx(className)}>
            {!hideHeader && (
              <thead>
                {tableInstance.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {expandableConfig.isEnabled && expandableConfig.showToggleColumn && (
                      <th style={{ width: 24, padding: 0 }} className="slds-text-align--center"></th>
                    )}

                    {headerGroup.headers.map((column) => {
                      const isSorted = column.column.getIsSorted();
                      const canSort = column.column.getCanSort();
                      const original = column.getContext().column.columnDef as TColumn<T>;

                      const { clickHandler: handleClickSorting, sortDirections } = getHeaderSorting(column);

                      return (
                        <th
                          key={column.id}
                          colSpan={column.colSpan}
                          className={cx(
                            {
                              'cell-action': canSort && !isEmpty,
                              sorted: isSorted,
                              unsorted: !isSorted,
                              'sorted-asc': isSorted === 'asc',
                              'sorted-desc': isSorted === 'desc',
                              'slds-text-align--center': original.align === 'center',
                              'slds-text-align--right': original.align === 'right',
                              'slds-text-align--left': original.align === 'left'
                            },
                            original.headerClassName
                          )}
                          onClick={handleClickSorting}
                          style={{ width: original.width }}
                        >
                          {canSort && !isEmpty ? (
                            <div className="slds-grid slds-grid--align-spread">
                              <div style={{ flex: '1 1 auto' }}>
                                {original.tooltip ? (
                                  <ComplexTooltip className="span" tooltip={original.tooltip} placement="top">
                                    {flexRender(column.column.columnDef.header, column.getContext())}
                                  </ComplexTooltip>
                                ) : (
                                  flexRender(column.column.columnDef.header, column.getContext())
                                )}
                              </div>

                              <Stack spacingInRems={1} alignItems="center" className="slds-m-left--xx-small">
                                <div
                                  className={cx('sort-icons', {
                                    asc: isSorted === 'asc',
                                    desc: isSorted === 'desc',
                                    both: sortDirections === 'both',
                                    one: sortDirections !== 'both'
                                  })}
                                >
                                  {(sortDirections === 'both' || sortDirections.includes('asc')) && (
                                    <i className="fa-solid fa-sort-up sort-up"></i>
                                  )}
                                  {(sortDirections === 'both' || sortDirections.includes('desc')) && (
                                    <i className="fa-solid fa-sort-down sort-down"></i>
                                  )}
                                </div>
                              </Stack>
                            </div>
                          ) : (
                            <>
                              {original.tooltip ? (
                                <ComplexTooltip className="span" tooltip={original.tooltip} placement="top">
                                  {flexRender(column.column.columnDef.header, column.getContext())}
                                </ComplexTooltip>
                              ) : (
                                flexRender(column.column.columnDef.header, column.getContext())
                              )}
                            </>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
            )}
            <tbody>
              {isLoading ? (
                <tr className="no-action">
                  <td colSpan={columnsLength} className="slds-text-align--center">
                    <SpinnerOverlay className="empty-content-wrapper" isLoading />
                  </td>
                </tr>
              ) : (
                <>
                  {!isEmpty ? (
                    tableInstance.getRowModel().rows.map((row, rowIndex) => {
                      const { isRowExpandable, handleClickExpand, isExpanded } = getExpandableRow(row);

                      const rowId = getRowKey ? getRowKey(row.original as T) : row.id;

                      const rows = tableInstance.getRowModel().rows;
                      const showInterstitialBeforeRow =
                        props.showInterstitialEvery &&
                        rows.length > props.showInterstitialEvery &&
                        rowIndex > 0 &&
                        rowIndex % props.showInterstitialEvery === 0;
                      const interstitialOffset = props.showInterstitialEvery
                        ? Math.floor(rowIndex / props.showInterstitialEvery) - 1
                        : 0;

                      const rowProps = getRowProps ? getRowProps(row.original as T) : {};

                      return (
                        <Fragment key={rowId}>
                          {showInterstitialBeforeRow ? (
                            <tr>
                              <td colSpan={columnsLength} className="slds-text-align--center slds-p-around--x-small">
                                {props.renderInterstitialContent ? (
                                  props.renderInterstitialContent(interstitialOffset, rowIndex)
                                ) : (
                                  <InContentAd />
                                )}
                              </td>
                            </tr>
                          ) : null}
                          <tr
                            {...rowProps}
                            onClick={
                              isRowExpandable
                                ? (e: React.MouseEvent) => {
                                    if (!isInteractiveElement(e.target)) {
                                      handleClickExpand();
                                    }
                                  }
                                : rowProps.onClick
                            }
                            style={{
                              ...(isRowExpandable ? { cursor: 'pointer' } : {}),
                              ...rowProps.style
                            }}
                          >
                            {expandable && expandableConfig.showToggleColumn && (
                              <td className="slds-text-align--center" style={{ padding: 0 }}>
                                {isRowExpandable && (
                                  <IconButton
                                    variant="text"
                                    size="small"
                                    icon={expandableConfig.icon!}
                                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                    onClick={handleClickExpand}
                                  />
                                )}
                              </td>
                            )}
                            {row.getAllCells().map((cell, cellIndex) => {
                              const column = cell.column.columnDef as TColumn<T>;
                              const isSorted = cell.column.getIsSorted();

                              const row = cell.getContext().row;

                              let value: any;

                              if (column.accessor) {
                                value =
                                  typeof column.accessor === 'function'
                                    ? column.accessor(row.original)
                                    : _.get(row.original, column.accessor);
                              } else {
                                value = cell.getValue();
                              }

                              const cellProps = getCellProps ? getCellProps(column, row.original as T) : {};

                              return (
                                <td
                                  key={cell.id}
                                  {...cellProps}
                                  className={cx(
                                    {
                                      'slds-text-align--center': column.align === 'center',
                                      'slds-text-align--right': column.align === 'right',
                                      'slds-text-align--left': column.align === 'left',
                                      'text-muted': muteValues?.includes(cell.renderValue()),
                                      sorted: isSorted
                                    },
                                    column.cellClassName,
                                    cellProps.className
                                  )}
                                  style={{ width: column.width, ...cellProps.style }}
                                >
                                  {column.cellRender
                                    ? flexRender(
                                        column.cellRender.bind(this, row.original, {
                                          dataTableRow: row,
                                          dataTableRowIndex: rowIndex,
                                          dataTableCellIndex: cellIndex,
                                          columns: tableInstance.getAllColumns(),
                                          value,
                                          isRowExpandable,
                                          isExpanded,
                                          toggleExpand: handleClickExpand,
                                          setExpansionType: expandableConfig?.onSetExpansionType
                                            ? (type: string) => expandableConfig.onSetExpansionType!(rowId, type)
                                            : undefined
                                        }),
                                        {}
                                      )
                                    : (cell.renderValue() as React.ReactNode)}
                                </td>
                              );
                            })}
                          </tr>

                          {isExpanded && (
                            <tr className="no-action">
                              <td colSpan={columnsLength}>{expandableConfig?.getChildren(row.original as T)}</td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  ) : (
                    <>
                      {emptyContent ? (
                        <tr className="no-action rio-full-height">
                          <td colSpan={columnsLength} className="slds-text-align--center">
                            <div className="empty-content-wrapper">{emptyContent}</div>
                          </td>
                        </tr>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </tbody>
            {footer && (
              <tfoot>
                <tr>
                  <td colSpan={columnsLength}>{footer}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Pagination>
    </div>
  );
}
